<template>
  <lb-drawer
    :value="value"
    :heading="dialogHeading"
    :width="drawerWidths.md"
    :Maxwidth="drawerWidths.md"
    :loading="loading"
    data-testid="setup-users__drawer"
    @input="onInput"
  >
    <template #body>
      <div class="user-form-dialog" data-testid="setup-users__drawer-body">
        <h5
          class="user-form-dialog__section-title"
          data-testid="setup-users__drawer-details-title"
        >
          Details
        </h5>

        <!-- Add mode: user picker (kept for when Add is enabled) -->
        <div
          v-if="isAdd"
          class="user-form-dialog__field"
          data-testid="setup-users__drawer-col-user"
        >
          <p class="user-form-dialog__label user-form-dialog__label--required ma-0">
            User
          </p>
          <lb-dropdown
            :value="form.user_id"
            :items="availableUsers"
            label=""
            :error="errors.user_id"
            :attach="false"
            itemtext="name"
            itemvalue="value"
            appendiconinner="mdi-chevron-down"
            placeholder="Select user"
            data-testid="setup-users__drawer-user-input"
            @input="onUserSelect"
          />
        </div>

        <!-- Edit mode: name and email are view-only, side by side -->
        <div
          v-else
          class="user-form-dialog__row"
          data-testid="setup-users__drawer-row-name-email"
        >
          <div class="user-form-dialog__row-item" data-testid="setup-users__drawer-col-name">
            <p class="user-form-dialog__label ma-0">Name</p>
            <lb-string
              :value="form.name"
              label=""
              placeholder="—"
              :disabled="true"
              data-testid="setup-users__drawer-name-input"
            />
          </div>

          <div class="user-form-dialog__row-item" data-testid="setup-users__drawer-col-email">
            <p class="user-form-dialog__label ma-0">Email</p>
            <lb-string
              :value="form.email"
              label=""
              placeholder="—"
              :disabled="true"
              data-testid="setup-users__drawer-email-input"
            />
          </div>
        </div>

        <div
          class="user-form-dialog__status"
          data-testid="setup-users__drawer-col-status"
        >
          <div class="user-form-dialog__status-copy">
            <p class="user-form-dialog__status-title ma-0">User status</p>
            <p class="user-form-dialog__status-desc ma-0">
              Disabled users keep their row but have no location access.
            </p>
          </div>
          <v-switch
            class="user-form-dialog__status-switch mt-0 pt-0"
            inset
            dense
            hide-details
            color="primary"
            :input-value="!!form.status"
            :disabled="loading"
            data-testid="setup-users__drawer-status-switch"
            @change="updateField('status', $event)"
          />
        </div>

        <div class="user-form-dialog__field" data-testid="setup-users__drawer-col-role">
          <p class="user-form-dialog__label user-form-dialog__label--required ma-0">
            Role
          </p>
          <lb-dropdown
            :value="form.role"
            :items="roleOptions"
            label=""
            :error="errors.role"
            :attach="false"
            itemtext="name"
            itemvalue="value"
            appendiconinner="mdi-chevron-down"
            placeholder="Select role"
            data-testid="setup-users__drawer-role-input"
            @input="onRoleChange"
          />
        </div>

        <div class="user-form-dialog__field" data-testid="setup-users__drawer-col-locations">
          <p class="user-form-dialog__label ma-0">Locations</p>
          <!-- Tooltip only while Controller (fbc_admin) locks locations; wrap so hover works on disabled control. -->
          <v-tooltip
            bottom
            content-class="tooltip-bottom"
            :disabled="!isControllerRole"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                data-testid="setup-users__drawer-locations-tooltip-wrap"
              >
                <ChipMultiSelect
                  :value="form.location_ids"
                  :items="locations"
                  label=""
                  :disabled="locationsDisabled"
                  data-testid="setup-users__drawer-locations"
                  @input="updateField('location_ids', $event)"
                />
              </div>
            </template>
            <span>Controllers are not limited to selected locations, so location selection is disabled.</span>
          </v-tooltip>
          <p
            v-if="errors.location_ids"
            class="user-form-dialog__error ma-0"
            data-testid="setup-users__drawer-locations-error"
          >
            {{ errors.location_ids }}
          </p>
        </div>
      </div>
    </template>

    <template #actions>
      <div class="user-form-dialog__actions">
        <p
          v-if="saveError"
          class="user-form-dialog__save-error ma-0"
          data-testid="setup-users__drawer-save-error"
        >
          {{ saveError }}
        </p>
        <div class="user-form-dialog__actions-row">
          <v-spacer />
          <v-btn
            color="primary"
            class="user-form-dialog__save-btn text-none white--text"
            :loading="loading"
            :disabled="loading"
            data-testid="setup-users__drawer-save-btn"
            @click="$emit('save')"
          >
            <span data-testid="setup-users__drawer-save-label">{{ saveLabel }}</span>
          </v-btn>
        </div>
      </div>
    </template>
  </lb-drawer>
</template>

<script>
import './UserFormDialog.scss';
import { drawerWidths } from '@/styles/design-tokens.js';
import { ChipMultiSelect } from '@/modules/financialclose/components';
import { ROLE_OPTIONS } from '../../utils/userConstants';
import { roleLabelForRole } from '../../utils/userHelpers';

export default {
  name: 'UserFormDialog',

  components: {
    ChipMultiSelect,
  },

  data() {
    return {
      drawerWidths,
    };
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'edit',
      validator: (v) => ['add', 'edit'].includes(v),
    },
    form: {
      type: Object,
      default: () => ({}),
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    saveError: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    locations: {
      type: Array,
      default: () => [],
    },
    availableUsers: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    isAdd() {
      return this.mode === 'add';
    },

    dialogHeading() {
      return this.isAdd ? 'Add user' : 'Edit user';
    },

    saveLabel() {
      return this.isAdd ? 'Add' : 'Save';
    },

    /**
     * TODO(phase-2): Every role is selectable. Controller here and FBC Admin on
     * the platform client are kept in sync by a temporary backend hack
     * (roleSync.js). Phase 2 must replace that with one role model.
     */
    roleOptions() {
      return ROLE_OPTIONS;
    },

    /** Locations are not editable while role is fbc_admin (role_label controller). */
    locationsDisabled() {
      return this.loading || this.isControllerRole;
    },

    /** Controller role: locations locked; tooltip explains why. */
    isControllerRole() {
      return this.form?.role === 'fbc_admin';
    },
  },

  methods: {
    onInput(open) {
      this.$emit('input', open);
    },

    updateField(key, value) {
      this.$emit('update:form', { ...this.form, [key]: value });
    },

    onRoleChange(role) {
      const selected = (this.roleOptions || []).find((opt) => opt.value === role);
      this.$emit('update:form', {
        ...this.form,
        role,
        role_label: selected?.role_label || roleLabelForRole(role),
      });
    },

    onUserSelect(userId) {
      const selected = (this.availableUsers || []).find(
        (u) => String(u.value) === String(userId)
      );
      this.$emit('update:form', {
        ...this.form,
        user_id: userId,
        name: selected?.labelName || selected?.name || '',
        email: selected?.email || '',
      });
    },
  },
};
</script>
